import { float, mix, mx_noise_float, normalize, positionWorld, sin, smoothstep, vec3 } from 'three/tsl';
import type { AtmosphereDef } from '../../engine/atmosphere/AtmosphereDef';
import type { LightRig } from '../../engine/lighting/LightRig';
import type { TerrainProfile } from '../../engine/terrain/TerrainProfile';
import {
  type BakedNoise,
  broadNoise,
  noiseWhen,
  reliefShade,
  ridgeOf,
  type SurfaceContext,
  type TerrainStyle,
} from '../../engine/terrain/TerrainStyle';
import type { GrassStyle, TreeStyle } from '../../engine/vegetation/VegetationStyle';
import type { HollowSceneDef } from '../scenes/HollowScene';
import { boulders } from './rocks';

type N = any;

/**
 * Glowmire: a 5 km marsh hollow with no sun at all. Every bit of light comes from
 * living things: moss, reeds, giant mushrooms, lantern stalks and drifting spores.
 * The whole marsh breathes as one: its glow swells everywhere at once, then sinks into
 * a deep dark where only the spores and the lantern stalks still shine, and blooms back.
 * Those are its days.
 */
export const GLOWMIRE_TERRAIN: TerrainProfile = {
  // Mostly low land just above the water line; the hills noise dips below it
  // everywhere, which scatters pools and channels across the marsh.
  continents: { wavelength: 5200, octaves: 4, lacunarity: 2.05, gain: 0.5 },
  continentHeight: 40,
  landBias: 0.3,
  mountainMask: { wavelength: 3800, octaves: 2, lacunarity: 2.1, gain: 0.5 },
  ridges: { wavelength: 1400, octaves: 7, lacunarity: 2.02, gain: 0.5 },
  ridgeHeight: 520,
  ridgeDamping: 2.2,
  hills: { wavelength: 260, octaves: 4, lacunarity: 2.05, gain: 0.5 },
  hillHeight: 16,
  detail: { wavelength: 40, octaves: 5, lacunarity: 2.07, gain: 0.48 },
  detailHeight: 1.6,
  // A marsh: little slope to cut, so little erosion, but its spires and rises gully.
  erosion: { wavelength: 230, octaves: 4, lacunarity: 1.97, gain: 0.55, strength: 0.3 },
  snowLine: 1e9,
};

/** Thin mist that glows faintly violet with spores where it is densest. */
export const GLOWMIRE_AIR: AtmosphereDef = {
  rayleigh: [1.6e-6, 2.4e-6, 4.4e-6],
  rayleighHeight: 3000,
  mie: 1.2e-5,
  mieHeight: 500,
  mieAbsorption: 0.3,
  mieG: 0.6,
  ambientScatter: 0.8,
  airGlow: [0.3e-5, 0.2e-5, 0.6e-5],
};

/** Glow phase (night level … 1) at a hollow-local position (the same everywhere: the marsh breathes as one). */
const phaseAt = (rig: LightRig, local: N): N => rig.glowAt(normalize(local));
/** Glow phase at the fragment being shaded (any material). */
const phaseHere = (rig: LightRig): N => phaseAt(rig, positionWorld.sub(rig.centerRel));
/** Hollow-local position of the fragment being shaded. */
const localHere = (rig: LightRig): N => positionWorld.sub(rig.centerRel);

const CYAN = vec3(0.12, 0.85, 0.8);
const VIOLET = vec3(0.55, 0.3, 1.0);
const GREEN = vec3(0.35, 1.0, 0.35);

/**
 * Glow colour drifting across the marsh: mostly cyan, with violet and green regions. With the
 * ground's context, its noise comes from the tiles (see GLOWMIRE_BAKED).
 */
const glowHue = (p: N, ctx?: SurfaceContext): N => {
  const a = broadNoise(ctx, p, 450, [1.3, 4.1, 2.7]);
  const b = broadNoise(ctx, p, 700, [7.7, 0.2, 5.5]);
  return mix(mix(CYAN, VIOLET, smoothstep(0.05, 0.4, a)), GREEN, smoothstep(0.2, 0.5, b).mul(0.7));
};

/** Large glowing moss fields (0..1): they draw the far side overhead. */
const fieldsAt = (p: N, ctx?: SurfaceContext): N =>
  smoothstep(-0.15, 0.35, broadNoise(ctx, p, 1300, [0.7, 3.3, 9.1]));
/** Soft base glow of the moss carpet (shared by the ground and the reeds on it). */
const FIELD_GLOW = 0.035;

/** The palette's broad noise terms, carried by the tiles (TerrainStyle.bakedNoise). */
const GLOWMIRE_BAKED: BakedNoise[] = [
  { wavelength: 450, offset: [1.3, 4.1, 2.7] },
  { wavelength: 700, offset: [7.7, 0.2, 5.5] },
  { wavelength: 1300, offset: [0.7, 3.3, 9.1] },
  { wavelength: 900, offset: [5.3, 1.1, 8.2] },
  { wavelength: 700 },
];

const glowmireStyle = (rig: LightRig): TerrainStyle => ({
  bakedNoise: GLOWMIRE_BAKED,
  forestMask(ctx) {
    const { p, h, slope } = ctx;
    // Mushroom groves keep to the gullies.
    const grove = broadNoise(ctx, p, 900, [5.3, 1.1, 8.2]).sub(ridgeOf(ctx).all.mul(0.2));
    return smoothstep(-0.05, 0.25, grove)
      .mul(float(1).sub(smoothstep(0.25, 0.45, slope)))
      .mul(float(1).sub(smoothstep(160, 260, h)));
  },

  land(ctx) {
    const { p, h, slope, near, mid } = ctx;
    const phase = phaseAt(rig, p);
    const macro = broadNoise(ctx, p, 700);
    const fine = mx_noise_float(p.mul(1 / 60));
    const patch = mx_noise_float(p.mul(1 / 9));
    // Close by only, in a branch (multiplying by 0 saves nothing).
    const grain = noiseWhen(p.mul(1 / 1.4), near).mul(near);

    // Moss carpets in deep blue-teal, drifting toward violet heath on the rises.
    const moss = mix(vec3(0.04, 0.09, 0.1), vec3(0.06, 0.07, 0.14), macro.mul(0.5).add(0.5));
    const heath = vec3(0.11, 0.065, 0.14);
    // The landform: moss fills the gullies, heath keeps to the crests.
    const ridge = ridgeOf(ctx);
    const gully = smoothstep(0.1, 0.65, ridge.all.negate());
    let ground: N = mix(
      moss,
      heath,
      smoothstep(0.1, 0.6, fine.add(h.mul(0.006)).add(ridge.all.mul(0.25))).mul(0.7),
    );
    ground = ground.mul(grain.mul(0.25).add(1)).mul(patch.mul(0.1).add(1));
    const mud = vec3(0.06, 0.055, 0.06);
    const band = sin(h.mul(0.35).add(fine.mul(5)))
      .mul(0.5)
      .add(0.5);
    const rock = mix(vec3(0.05, 0.045, 0.08), vec3(0.13, 0.1, 0.17), band).mul(grain.mul(0.3).add(1));
    const grove = mix(vec3(0.04, 0.03, 0.07), vec3(0.06, 0.035, 0.09), fine.mul(0.5).add(0.5));
    let land: N = mix(ground, grove, this.forestMask(ctx).mul(0.8));
    land = mix(land, mud, float(1).sub(smoothstep(0.6, 2.5, h.add(fine.mul(0.8)))));
    const rocky = smoothstep(0.22, 0.4, slope.add(fine.mul(0.05)).add(ridge.all.mul(0.09)));
    land = mix(land, rock, rocky);
    // Narrow gullies read as dark creases.
    const crease = smoothstep(0.15, 0.8, ridge.narrow.negate());
    land = land.mul(float(1).sub(crease.mul(0.3))).mul(reliefShade(ridge.relief));

    // Large glowing fields (they draw the far side overhead), patches within them,
    // pool rims, rock veins and close-up specks.
    const fields = fieldsAt(p, ctx);
    const patches = smoothstep(0.25, 0.55, mx_noise_float(p.mul(1 / 90).add(vec3(2.2, 0, 1.7))))
      .mul(smoothstep(-0.1, 0.35, patch.add(fine.mul(0.3))).mul(near.mul(0.6).add(0.4)))
      .mul(float(1).sub(rocky));
    const shore = float(1)
      .sub(smoothstep(0.3, 1.8, h))
      .mul(smoothstep(-0.2, 0.1, h))
      .mul(mid.mul(0.6).add(0.4));
    // Glowing veins in the rock faces of the spires.
    const veinsHere = rocky.mul(mid);
    const veins = smoothstep(0.9, 0.98, float(1).sub(noiseWhen(p.mul(1 / 12), veinsHere).abs())).mul(
      veinsHere,
    );
    const speck = smoothstep(0.62, 0.7, noiseWhen(p.mul(3.1), near)).mul(near);
    // Glowing rills: the moss down the narrow gullies glows, drawing the drainage overhead.
    const rills = crease.mul(gully).mul(float(1).sub(rocky));
    const hue = glowHue(p, ctx);
    const emissive = hue
      .mul(
        fields
          .mul(float(1).sub(rocky))
          .mul(patches.mul(0.2).add(FIELD_GLOW))
          .add(shore.mul(0.45))
          .add(speck.mul(0.5))
          .add(veins.mul(0.5))
          .add(rills.mul(0.06)),
      )
      .mul(phase);
    const roughness = mix(float(0.85), float(0.5), smoothstep(0.5, 0, h));
    return { albedo: land, roughness, emissive };
  },

  water: {
    shallow: [0.008, 0.03, 0.045],
    deep: [0.003, 0.008, 0.02],
    depthScale: 5,
    // Plankton glow in the shallows.
    emissive: (ctx) => {
      const { p, h, near } = ctx;
      return glowHue(p, ctx)
        .mul(float(1).sub(smoothstep(0, 3, h.negate())))
        .mul(
          smoothstep(-0.3, 0.4, mx_noise_float(p.mul(1 / 14)))
            .mul(near.mul(0.6).add(0.4))
            .add(0.25),
        )
        .mul(0.22)
        .mul(phaseAt(rig, p));
    },
  },
});

const glowmireGrass = (rig: LightRig): GrassStyle => ({
  lush: [0.03, 0.08, 0.1],
  meadow: [0.08, 0.07, 0.14],
  tip: [0.14, 0.24, 0.28],
  roughness: 0.55,
  minHeight: 0.4,
  height: 0.85,
  // Reeds share the moss glow at their feet, and their tips glow in drifts.
  glow: () => {
    const p = localHere(rig);
    return glowHue(p)
      .mul(fieldsAt(p).mul(FIELD_GLOW * 1.1))
      .mul(phaseHere(rig));
  },
  tipGlow: () => {
    const p = localHere(rig);
    const drift = smoothstep(0.0, 0.45, mx_noise_float(p.mul(1 / 22).add(vec3(4.4, 1.9, 0.3))));
    return glowHue(p).mul(drift.mul(0.5).add(0.04)).mul(phaseHere(rig));
  },
});

const glowmireTrees = (rig: LightRig): TreeStyle => ({
  a: {
    shape: 'mushroom',
    bark: [0.15, 0.13, 0.19],
    crown: [
      [0.2, 0.05, 0.3],
      [0.05, 0.08, 0.28],
    ],
    bendUp: 0.2,
    scale: 1.35,
    glow: () => mix(CYAN, VIOLET, 0.35).mul(1.5).mul(phaseHere(rig)),
    crownGlow: 0,
  },
  b: {
    shape: 'lantern',
    bark: [0.06, 0.08, 0.07],
    crown: [
      [0.05, 0.14, 0.12],
      [0.12, 0.16, 0.09],
    ],
    bendUp: 0.3,
    // The lanterns dim with the marsh, but keep a little light through its dark.
    glow: () => vec3(1.0, 0.7, 0.3).mul(2.6).mul(phaseHere(rig).mul(0.85).add(0.15)),
    crownGlow: 0.05,
    // Each bulb lights a pool of ground around its stalk.
    light: { height: 6.9, radius: 12, gain: 14, hooded: true },
  },
  aShare: [0.6, 0.35],
  loneTrees: 0.02,
  minHeight: 0.8,
});

export const GLOWMIRE: HollowSceneDef = {
  id: 'glowmire',
  name: 'Glowmire',
  center: [7.3e6, -2.1e6, 5.6e6],
  radius: 5_000,
  seed: 23,
  sun: null,
  terrain: GLOWMIRE_TERRAIN,
  air: GLOWMIRE_AIR,
  clouds: null,
  veil: {
    litFraction: 0.5,
    softness: 0.28,
    dayLength: 480,
    spinAxis: [0.8, 0.3, -0.5],
    duskColor: [0.6, 0.3, 1.0],
    // (The dark itself is the glow's: these only tint it, violet as it fades and blooms.)
    nightAmbient: [0.55, 0.5, 0.9],
    rimAmbient: [0.8, 0.55, 1.4],
    rimAmbientStrength: 0.45,
  },
  // Its dark is a fiftieth of its light (it was a seventh, and the night side never felt like night).
  glow: { color: [0.4, 0.62, 1.0], day: 0.45, night: 0.02, pulse: 'unified' },
  worldAlbedo: [0.07, 0.08, 0.11],
  style: glowmireStyle,
  grass: glowmireGrass,
  trees: glowmireTrees,
  rocks: () =>
    boulders(
      [
        [0.08, 0.1, 0.12],
        [0.12, 0.1, 0.16],
      ],
      [
        [0.05, 0.08, 0.08],
        [0.07, 0.1, 0.09],
      ],
      { scale: 0.9, lone: 0.008, minHeight: 0.8 },
    ),
  motes: (rig) => ({
    count: 2600,
    radius: 40,
    size: [0.06, 0.16],
    colors: [
      [0.4, 1.0, 0.8],
      [0.75, 0.55, 1.0],
    ],
    brightness: 2.0,
    drift: 0.25,
    // Spores shine brighter while the marsh around them sleeps.
    glow: () => mix(float(1.4), float(0.7), phaseAt(rig, rig.centerRel.negate())),
  }),
  spawn: { dir: [-0.3, 0.7, 0.6], minHeight: 2, maxHeight: 40 },
  exposureKey: 7,
  hints: [
    'No sun here: everything is lit by living glow.',
    'Hold T: the whole marsh brightens and darkens as one, like a slow breath.',
  ],
  waterSkyTint: [0.7, 0.8, 1.2],
  ambience: {
    wind: { gain: 0.07, cutoff: 350 },
    drone: { freqs: [110, 164.8, 220], gain: 0.022 },
    events: [
      { kind: 'chime', rate: 0.16, gain: 0.045 },
      { kind: 'cricket', rate: 0.6, gain: 0.01, when: 'night' },
    ],
    echo: 0.35,
  },
};
